import { createRoot } from 'react-dom/client'
import { StrictMode, useCallback, useEffect } from 'react'
import './index.css'
import './i18n/config' // Initialize i18n
import { AnalyticsEvent, AnalyticsTrackFunction, EventParams } from './types/analytics'
import { EventAnalytics } from './lib/analytics'
import { debugLog } from './lib/debug'
import { isFeatureEnabled } from './lib/feature-flags'
import App from './App'

// Initialize accessibility testing tool in development
if (import.meta.env.DEV) {
  import('react' as any).then((React) => {
    import('react-dom' as any).then((ReactDOM) => {
      import('@axe-core/react')
        .then((axe) => {
          axe.default(React, ReactDOM, 1000)
          console.log('✅ Accessibility testing with @axe-core/react is active')
          console.log('📋 Check browser console for accessibility violations')
        })
        .catch(() => {
          console.log('ℹ️  Accessibility testing not available (install @axe-core/react)')
        })
    })
  })
}

// Microsoft Clarity removed - we don't use third-party analytics

// Global error handling for all environments
// Handle AbortErrors gracefully - they're expected during cleanup
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.name === 'AbortError') {
    event.preventDefault() // Prevent console logging
    return
  }
})

// Additional error handling for production
if (import.meta.env.PROD) {
  window.addEventListener('error', (event) => {
    // Ignore AbortErrors - they're expected during cleanup
    if (event.error?.name === 'AbortError') return

    // Only log if there's actual error information
    if (event.error) {
      console.error('Global error:', event.error)
    } else if (event.message && event.message !== 'Script error.') {
      console.error('Global error:', event.message)
    }
    // Suppress generic "Script error." messages
  })

  window.addEventListener('unhandledrejection', (event) => {
    // Ignore AbortErrors - they're expected during cleanup
    if (event.reason?.name === 'AbortError') {
      event.preventDefault()
      return
    }

    if (event.reason) {
      console.error('Unhandled rejection:', event.reason)
    }
  })
}

// Analytics tracking function
const useAnalytics = () => {
  const trackEvent = useCallback<AnalyticsTrackFunction>((...args) => {
    if (!isFeatureEnabled('analytics')) return

    try {
      let event: AnalyticsEvent

      // Handle both function signatures
      if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
        // First overload: single object parameter
        event = args[0] as AnalyticsEvent
      } else if (args.length >= 2) {
        // Second overload: multiple parameters
        event = {
          category: args[0] as EventAnalytics,
          action: String(args[1]),
          value: args[2] as string | number | boolean | null | undefined,
          params: args[3] as EventParams | undefined,
        }
      } else {
        throw new Error('Invalid arguments for trackEvent')
      }

      const eventData: EventParams = {
        ...(event.params || {}),
        timestamp: new Date().toISOString(),
        path: window.location.pathname,
      }

      debugLog('analytics', `${event.category}.${event.action}`, eventData)

      if (window.analytics?.trackEvent) {
        try {
          // Only pass value if it's a string or number
          const value =
            typeof event.value === 'boolean'
              ? event.value
                ? 'true'
                : 'false'
              : (event.value ?? undefined)

          window.analytics.trackEvent(
            event.category,
            event.action,
            value as string | number | undefined,
            eventData,
          )
        } catch (err) {
          console.error('Error in analytics tracking:', err)
        }
      }
    } catch (error) {
      console.error('Error tracking event:', error)
    }
  }, [])

  const trackAppStart = useCallback(() => {
    if (!isFeatureEnabled('analytics')) return

    // Track application start
    trackEvent({ category: 'engagement', action: 'app_start' })

    // Track performance metrics
    if (typeof window !== 'undefined' && 'performance' in window) {
      const onLoad = () => {
        trackEvent({
          category: 'performance',
          action: 'app_loaded',
          params: { loadTime: Math.round(window.performance.now()) },
        })
        // Remove the event listener after it fires
        window.removeEventListener('load', onLoad)
      }

      window.addEventListener('load', onLoad)
    }
  }, [trackEvent])

  return {
    trackEvent: trackEvent as AnalyticsTrackFunction,
    trackAppStart,
  }
}

// Main App Wrapper
export const AppWithAnalytics = () => {
  const { trackEvent, trackAppStart } = useAnalytics()

  // Track initial page load (auth initialization handled by AuthContext)
  useEffect(() => {
    trackAppStart()

    if (isFeatureEnabled('analytics')) {
      trackEvent({
        category: 'page_view',
        action: 'page_load',
        params: {
          path: window.location.pathname,
          referrer: document.referrer,
        },
      })
    }
  }, [trackEvent, trackAppStart])

  // Track route changes
  useEffect(() => {
    if (!isFeatureEnabled('analytics')) return

    const handleRouteChange = () => {
      trackEvent({
        category: 'page_view',
        action: 'route_change',
        params: {
          path: window.location.pathname,
        },
      })
    }

    // Listen for route changes
    window.addEventListener('popstate', handleRouteChange)

    return () => {
      window.removeEventListener('popstate', handleRouteChange)
    }
  }, [trackEvent])

  return (
    <StrictMode>
      <App />
    </StrictMode>
  )
}

// Create root and render app
const rootElement = document.getElementById('root')
if (rootElement) {
  const root = createRoot(rootElement)
  root.render(<AppWithAnalytics />)
} else {
  console.error('Failed to find the root element')
}
